import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/Logo";
import { CTAButton } from "@/components/CTAButton";
import { useAudit } from "@/components/AuditContext";
import { SERVICES, SERVICE_ORDER } from "@/lib/servicesData";

const SERVICE_LINKS = SERVICE_ORDER.map((slug) => ({ to: `/${slug}`, label: SERVICES[slug].navLabel }));

// « Ressources » — contenus éditoriaux.
const RESOURCE_LINKS = [
  { to: "/guide", label: "Guide" },
  { to: "/actualites", label: "Actualités" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [ressourcesOpen, setRessourcesOpen] = useState(false);
  const [mobileSolutions, setMobileSolutions] = useState(false);
  const [mobileRessources, setMobileRessources] = useState(false);
  const { openAudit } = useAudit();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setSolutionsOpen(false);
    setRessourcesOpen(false);
  }, [pathname]);

  const isSolutionsActive = pathname === "/ce-que-vous-obtenez" || SERVICE_LINKS.some((s) => s.to === pathname);
  const isRessourcesActive = pathname.startsWith("/guide") || pathname.startsWith("/actualites");

  const navLink = (active) =>
    cn("link-underline text-sm font-medium transition-colors", active ? "text-navy" : "text-slate-500 hover:text-navy");
  const triggerCls = (active) =>
    cn("flex items-center gap-1 text-sm font-medium transition-colors", active ? "text-navy" : "text-slate-500 hover:text-navy");
  const itemCls = "block px-4 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-[#f8fafc] hover:text-navy transition-colors";

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300",
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-slate-200 shadow-[0_4px_30px_-20px_rgba(7,27,58,0.5)]" : "bg-white/70 backdrop-blur-md border-b border-transparent"
      )}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" aria-label="Umbo, accueil">
          <Logo variant="light" />
        </Link>

        <nav className="hidden xl:flex items-center gap-3 whitespace-nowrap" aria-label="Navigation principale">
          <Link to="/offres" data-testid="nav-offres" className={navLink(pathname === "/offres")}>Offres</Link>

          {/* Solutions — Votre solution + tous les services */}
          <div className="relative" onMouseEnter={() => setSolutionsOpen(true)} onMouseLeave={() => setSolutionsOpen(false)}>
            <button
              className={triggerCls(isSolutionsActive)}
              onClick={() => setSolutionsOpen((v) => !v)}
              data-testid="nav-solutions"
              aria-expanded={solutionsOpen}
            >
              Solutions <ChevronDown className={cn("w-4 h-4 transition-transform", solutionsOpen && "rotate-180")} />
            </button>
            <AnimatePresence>
              {solutionsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                >
                  <div className="w-72 bg-white rounded-xl border border-slate-200 shadow-xl p-2" data-testid="solutions-dropdown">
                    <Link to="/ce-que-vous-obtenez" className="block px-4 py-3 rounded-lg hover:bg-[#f8fafc] transition-colors group" data-testid="nav-solution-obtenez">
                      <span className="block text-sm font-semibold text-navy">Votre solution</span>
                      <span className="block mt-0.5 text-xs text-slate-500 leading-snug">Tout ce que vous obtenez, en un coup d'œil</span>
                    </Link>
                    <div className="my-1.5 border-t border-slate-100" />
                    <p className="px-4 pt-1 pb-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Nos services</p>
                    {SERVICE_LINKS.map((s) => (
                      <Link key={s.to} to={s.to} className={itemCls} data-testid={`nav-service-${s.to.slice(1)}`}>{s.label}</Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/pourquoi-umbo-cyber" data-testid="nav-pourquoi" className={navLink(pathname === "/pourquoi-umbo-cyber")}>Pourquoi Umbo</Link>

          {/* Ressources — Guide + Actualités */}
          <div className="relative" onMouseEnter={() => setRessourcesOpen(true)} onMouseLeave={() => setRessourcesOpen(false)}>
            <button
              className={triggerCls(isRessourcesActive)}
              onClick={() => setRessourcesOpen((v) => !v)}
              data-testid="nav-ressources"
              aria-expanded={ressourcesOpen}
            >
              Ressources <ChevronDown className={cn("w-4 h-4 transition-transform", ressourcesOpen && "rotate-180")} />
            </button>
            <AnimatePresence>
              {ressourcesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                >
                  <div className="w-56 bg-white rounded-xl border border-slate-200 shadow-xl p-2" data-testid="ressources-dropdown">
                    {RESOURCE_LINKS.map((r) => (
                      <Link key={r.to} to={r.to} className={itemCls} data-testid={`nav-ressource-${r.to.slice(1)}`}>{r.label}</Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/partenaires" data-testid="nav-partenaires" className={navLink(pathname === "/partenaires")}>Partenaires</Link>
          <Link to="/contact" data-testid="nav-contact" className={navLink(pathname === "/contact")}>Contact</Link>
          <Link
            to="/promo"
            data-testid="nav-promo"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors",
              pathname === "/promo" ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            )}
          >
            <Sparkles className="w-3.5 h-3.5" /> Audit −80 %
          </Link>
        </nav>

        <div className="hidden xl:block shrink-0"><CTAButton onClick={openAudit} data-testid="header-audit-cta">Échange gratuit</CTAButton></div>

        <button className="xl:hidden text-navy p-2" onClick={() => setMobile((v) => !v)} aria-label="Menu" data-testid="mobile-menu-toggle">
          {mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="xl:hidden overflow-hidden bg-white border-b border-slate-200">
            <div className="px-5 py-6 flex flex-col gap-1">
              <Link to="/offres" className="text-base font-medium text-navy py-2.5">Offres</Link>

              {/* Solutions (dépliable) */}
              <button className="flex items-center justify-between text-base font-medium text-navy py-2.5" onClick={() => setMobileSolutions((v) => !v)} data-testid="mobile-nav-solutions" aria-expanded={mobileSolutions}>
                Solutions <ChevronDown className={cn("w-4 h-4 transition-transform", mobileSolutions && "rotate-180")} />
              </button>
              <AnimatePresence>
                {mobileSolutions && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pl-4 flex flex-col border-l border-slate-100 ml-1">
                    <Link to="/ce-que-vous-obtenez" className="text-sm font-medium text-navy py-2">Votre solution</Link>
                    {SERVICE_LINKS.map((s) => (<Link key={s.to} to={s.to} className="text-sm text-slate-600 py-2">{s.label}</Link>))}
                  </motion.div>
                )}
              </AnimatePresence>

              <Link to="/pourquoi-umbo-cyber" className="text-base font-medium text-navy py-2.5">Pourquoi Umbo</Link>

              {/* Ressources (dépliable) */}
              <button className="flex items-center justify-between text-base font-medium text-navy py-2.5" onClick={() => setMobileRessources((v) => !v)} data-testid="mobile-nav-ressources" aria-expanded={mobileRessources}>
                Ressources <ChevronDown className={cn("w-4 h-4 transition-transform", mobileRessources && "rotate-180")} />
              </button>
              <AnimatePresence>
                {mobileRessources && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pl-4 flex flex-col border-l border-slate-100 ml-1">
                    {RESOURCE_LINKS.map((r) => (<Link key={r.to} to={r.to} className="text-sm text-slate-600 py-2">{r.label}</Link>))}
                  </motion.div>
                )}
              </AnimatePresence>

              <Link to="/partenaires" className="text-base font-medium text-navy py-2.5">Partenaires</Link>
              <Link to="/contact" className="text-base font-medium text-navy py-2.5">Contact</Link>
              <Link to="/promo" data-testid="mobile-nav-promo" className="mt-2 w-fit inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold px-4 py-2.5 text-base">
                <Sparkles className="w-4 h-4" /> Audit cyber −80 % · 100 €
              </Link>
              <CTAButton onClick={openAudit} className="w-full mt-3" data-testid="mobile-audit-cta">Échange gratuit</CTAButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
